import React, { useCallback, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  TouchableOpacity,
} from 'react-native';
import { useRoute, useFocusEffect, RouteProp } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';

import { RootState, AppDispatch } from '@/store';
import { getCallDetail, clearDetail } from '@/store/slices/callReviewSlice';
import { DashboardStackParamList } from '@/types/navigation';
import { CallScoreBand } from '@/types/api';
import { colors, font, space, radius, shadow } from '@/theme/chalkCourt';
import AudioPlayer from './AudioPlayer';
import AssigneePickerModal from '@/components/callperformance/AssigneePickerModal';

type CallDetailRoute = RouteProp<DashboardStackParamList, 'CallDetail'>;

const bandColor = (band?: CallScoreBand): string => {
  switch (band) {
    case 'good':
      return colors.court;
    case 'okay':
      return colors.warn;
    default:
      return colors.accent;
  }
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <View style={styles.section}>
    <Text style={styles.sectionTitle}>{title}</Text>
    {children}
  </View>
);

const CallDetailScreen: React.FC = () => {
  const route = useRoute<CallDetailRoute>();
  const { callUuid } = route.params;
  const dispatch = useDispatch<AppDispatch>();
  const { detail, detailLoading, detailError } = useSelector((s: RootState) => s.callReview);
  const isAdmin = useSelector((s: RootState) => !!s.auth.user?.employee?.admin);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [assignedNote, setAssignedNote] = useState<string | null>(null);

  useFocusEffect(
    useCallback(() => {
      dispatch(getCallDetail(callUuid));
      return () => {
        dispatch(clearDetail());
      };
    }, [dispatch, callUuid])
  );

  if (detailLoading && !detail) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color={colors.accent} />
      </View>
    );
  }

  if (detailError) {
    return (
      <View style={styles.centered}>
        <Text style={styles.stateTitle}>Couldn't load the call</Text>
        <Text style={styles.stateBody}>{detailError}</Text>
      </View>
    );
  }

  if (!detail || !detail.available) {
    return (
      <View style={styles.centered}>
        <Text style={styles.stateTitle}>Call detail unavailable</Text>
        <Text style={styles.stateBody}>This call couldn't be loaded right now.</Text>
      </View>
    );
  }

  const scoreColor = bandColor(detail.scoreBand);

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      {/* Header */}
      <View style={styles.card}>
        <View style={styles.headerRow}>
          <View style={styles.headerLeft}>
            {!!detail.callType && (
              <View style={styles.typeBadge}>
                <Text style={styles.typeBadgeText}>{detail.callType}</Text>
              </View>
            )}
            {!!detail.direction && <Text style={styles.direction}>{detail.direction}</Text>}
          </View>
          <Text style={[styles.score, { color: scoreColor }]}>
            {detail.scorePct}%{detail.staffGrade ? ` ${detail.staffGrade}` : ''}
          </Text>
        </View>
        <Text style={styles.metaLine}>
          {detail.startTimeDisplay}
          {detail.durationLabel ? ` · ${detail.durationLabel}` : ''}
        </Text>
        {!!detail.employeeName && <Text style={styles.assignee}>Staff: {detail.employeeName}</Text>}
        {isAdmin && (
          <TouchableOpacity style={styles.assignBtn} onPress={() => setPickerOpen(true)}>
            <Text style={styles.assignBtnText}>
              {detail.employeeId ? 'Reassign staff' : 'Assign to staff'}
            </Text>
          </TouchableOpacity>
        )}
        {assignedNote && <Text style={styles.assignedNote}>✓ Assigned to {assignedNote}</Text>}
      </View>

      {detail.flagged && (
        <View style={styles.flagBanner}>
          <Text style={styles.flagText}>⚑ Flagged for review{detail.flagReason ? `: ${detail.flagReason}` : ''}</Text>
        </View>
      )}

      {/* Recording */}
      <Section title="Recording">
        <AudioPlayer callUuid={callUuid} />
      </Section>

      {/* Summary */}
      {!!detail.summary && (
        <Section title="Summary">
          <Text style={styles.body}>{detail.summary}</Text>
        </Section>
      )}

      {/* Manager note */}
      {!!detail.managerNote && (
        <Section title="Manager Note">
          <Text style={styles.body}>{detail.managerNote}</Text>
        </Section>
      )}

      {/* Sentiment / urgency */}
      {(detail.sentiment || detail.urgencyLevel || detail.requiresEscalation) && (
        <Section title="Signals">
          <View style={styles.chipRow}>
            {!!detail.sentiment && <Chip label={`Sentiment: ${detail.sentiment}`} />}
            {!!detail.urgencyLevel && <Chip label={`Urgency: ${detail.urgencyLevel}`} />}
            {detail.requiresEscalation && <Chip label="Needs escalation" danger />}
          </View>
        </Section>
      )}

      {/* Strengths */}
      {detail.strengths.length > 0 && (
        <Section title="Strengths">
          {detail.strengths.map((s, i) => (
            <Text key={i} style={[styles.bullet, styles.bulletGood]}>
              • {s}
            </Text>
          ))}
        </Section>
      )}

      {/* Improvement areas */}
      {detail.improvementAreas.length > 0 && (
        <Section title="Areas for Improvement">
          {detail.improvementAreas.map((s, i) => (
            <Text key={i} style={[styles.bullet, styles.bulletGrow]}>
              • {s}
            </Text>
          ))}
        </Section>
      )}

      {/* Quality dimensions */}
      {detail.dimensions.length > 0 && (
        <Section title="Quality Breakdown">
          {detail.dimensions.map((d, i) => (
            <View key={i} style={styles.dim}>
              <View style={styles.dimHeader}>
                <Text style={styles.dimName}>{d.name}</Text>
                <Text style={[styles.dimPct, { color: bandColor(d.band) }]}>{d.scorePct}%</Text>
              </View>
              <View style={styles.dimTrack}>
                <View
                  style={[styles.dimFill, { width: `${Math.max(0, Math.min(100, d.scorePct))}%`, backgroundColor: bandColor(d.band) }]}
                />
              </View>
              {!!d.notes && <Text style={styles.dimNotes}>{d.notes}</Text>}
            </View>
          ))}
        </Section>
      )}

      <AssigneePickerModal
        visible={pickerOpen}
        callUuid={callUuid}
        currentEmployeeId={detail.employeeId ?? null}
        onClose={() => setPickerOpen(false)}
        onAssigned={(name) => {
          setAssignedNote(name ?? 'staff');
          dispatch(getCallDetail(callUuid)); // refresh the assignment
        }}
      />
    </ScrollView>
  );
};

const Chip: React.FC<{ label: string; danger?: boolean }> = ({ label, danger }) => (
  <View style={[styles.chip, danger && styles.chipDanger]}>
    <Text style={[styles.chipText, danger && styles.chipTextDanger]}>{label}</Text>
  </View>
);

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.bg },
  content: { padding: space.lg, paddingBottom: space.xxl },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.bg,
    padding: space.xl,
    gap: space.sm,
  },
  stateTitle: { fontFamily: font.displayBold, fontSize: 20, color: colors.ink, textAlign: 'center' },
  stateBody: { fontFamily: font.body, fontSize: 15, color: colors.inkSoft, textAlign: 'center' },
  card: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    padding: space.lg,
    marginBottom: space.lg,
    ...shadow.card,
  },
  headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  headerLeft: { flexDirection: 'row', alignItems: 'center', gap: space.sm, flex: 1 },
  typeBadge: {
    paddingHorizontal: space.sm,
    paddingVertical: 2,
    borderRadius: radius.sm,
    backgroundColor: colors.surfaceSunk,
    borderWidth: 1,
    borderColor: colors.line,
  },
  typeBadgeText: { fontFamily: font.bodySemi, fontSize: 12, color: colors.inkSoft },
  direction: { fontFamily: font.bodyMed, fontSize: 13, color: colors.inkFaint, textTransform: 'capitalize' },
  score: { fontFamily: font.display, fontSize: 28 },
  metaLine: { fontFamily: font.bodyMed, fontSize: 14, color: colors.inkSoft, marginTop: space.sm },
  assignee: { fontFamily: font.bodySemi, fontSize: 14, color: colors.ink, marginTop: space.xs },
  assignBtn: {
    marginTop: space.md,
    alignSelf: 'flex-start',
    paddingHorizontal: space.lg,
    paddingVertical: space.sm,
    borderRadius: radius.pill,
    borderWidth: 1,
    borderColor: colors.accent,
  },
  assignBtnText: { fontFamily: font.bodyBold, fontSize: 14, color: colors.accent },
  assignedNote: { fontFamily: font.bodySemi, fontSize: 13, color: colors.court, marginTop: space.sm },
  flagBanner: {
    backgroundColor: colors.warnSoft,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.lg,
  },
  flagText: { fontFamily: font.bodySemi, fontSize: 14, color: colors.warn },
  section: {
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    padding: space.lg,
    marginBottom: space.lg,
    ...shadow.card,
  },
  sectionTitle: {
    fontFamily: font.displayBold,
    fontSize: 17,
    color: colors.ink,
    marginBottom: space.sm,
  },
  body: { fontFamily: font.body, fontSize: 15, color: colors.inkSoft, lineHeight: 21 },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: space.sm },
  chip: {
    paddingHorizontal: space.md,
    paddingVertical: space.xs,
    borderRadius: radius.pill,
    backgroundColor: colors.surfaceSunk,
  },
  chipDanger: { backgroundColor: colors.accentSoft },
  chipText: { fontFamily: font.bodySemi, fontSize: 13, color: colors.inkSoft, textTransform: 'capitalize' },
  chipTextDanger: { color: colors.accentDark },
  bullet: { fontFamily: font.body, fontSize: 15, lineHeight: 22, marginBottom: space.xs },
  bulletGood: { color: colors.court },
  bulletGrow: { color: colors.inkSoft },
  dim: { marginBottom: space.md },
  dimHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  dimName: { fontFamily: font.bodySemi, fontSize: 14, color: colors.ink },
  dimPct: { fontFamily: font.bodyBold, fontSize: 14 },
  dimTrack: {
    height: 8,
    borderRadius: radius.pill,
    backgroundColor: colors.line,
    overflow: 'hidden',
    marginTop: space.xs,
  },
  dimFill: { height: '100%', borderRadius: radius.pill },
  dimNotes: { fontFamily: font.body, fontSize: 13, color: colors.inkFaint, marginTop: space.xs },
});

export default CallDetailScreen;
